import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog, DialogTitle } from '@mui/material';

import { createParticipantBarriers } from '../../../../store/participantService';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../utils/userRoles';
import Button from '../../../formElements/Button';

import SelectBarriersTableManagement from './SelectBarriersTableManagement';
import { validate } from './validateSelectBarriersEditor';

import form from '../../../../commonStyles/formStyles.module.css';
import modal from '../../../../commonStyles/modal.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

const SelectBarriersEditor = ({
    open,
    onBarrierEditor,
    barrierNameDetails,
    currentParticipant
}) => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const [isOpen, setIsOpen] = React.useState(false);
    const [rowsUpdated, setRowsUpdated] = useState([]);
    const [invalidDate, setInvalidDate] = React.useState(false);
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [errors, setErrors] = useState({});

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        setIsOpen(open);
    }, [open]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === `Participant barrier(s) created`) onCreated();
    }, [successMessage]);

    // HELPER FNS
    const clearError = (key) => {
        setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    const clearData = () => onBarrierEditor(false);

    // EVENT HANDLERS
    const onClose = (e, reason) => {
        if (reason === 'backdropClick') return;
        clearData();
    };

    const onCancel = () => clearData();

    const onCreated = () => {
        setRowsUpdated([]);
        onBarrierEditor(false);
    };

    const onRowsUpdated = (rowsUpdated, invalidDate) => {
        invalidDate ? setInvalidDate(true) : setInvalidDate(false);
        setRowsUpdated(rowsUpdated);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const participantBarriers = rowsUpdated.filter((el) => el.update === true);
        setIsUpdateButtonDisabled(true);
        const newErrors = validate(participantBarriers);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        const newBarriers = participantBarriers.map((el) => ({
            id: null,
            barrierId: el.id,
            participantId: currentParticipant.id,
            priority: el.priority,
            startDate: el.startDate,
            dueDate: el.dueDate
        }));
        dispatch(createParticipantBarriers({ barriers: newBarriers }));
    };

    // RENDER
    return (
        <Dialog
            className="muiDialogWrapperLarge"
            open={isOpen}
            onClose={onClose}
            aria-labelledby="form-dialog-title">
            <div className={modal.modalHeader}>
                <DialogTitle id="form-dialog-title">Diagnostic Suggested Barriers</DialogTitle>
            </div>
            <div className={modal.modalContent}>
                <form onSubmit={onSubmit} className={form.formWrapper} data-testid="form_start">
                    <p>
                        Your answers to the Diagnostic Questionnaire have given the below suggestion
                        for Barriers.
                        <br /> Please select the Barriers which are applicable.
                    </p>
                    <SelectBarriersTableManagement
                        onRowsUpdated={onRowsUpdated}
                        barrierNameDetails={barrierNameDetails}
                    />
                    <div className={modal.actionButtons}>
                        <Button
                            id="selectBarriersEditorUpdateButton"
                            disabled={
                                !hasRole(acceptedRoles, roles) ||
                                invalidDate ||
                                isUpdateButtonDisabled
                            }
                            error={errors?.button}
                            clearError={() => clearError('button')}
                            content="Update"
                            customErrorClass="formActionsError"
                        />
                        <Button
                            id="selectBarriersEditorCancelButton"
                            type="button"
                            content="Cancel"
                            onClick={onCancel}
                        />
                    </div>
                </form>
            </div>
        </Dialog>
    );
};

SelectBarriersEditor.propTypes = {
    open: PropTypes.bool.isRequired,
    onBarrierEditor: PropTypes.func,
    barrierNameDetails: PropTypes.array.isRequired,
    currentParticipant: PropTypes.object.isRequired
};

export default SelectBarriersEditor;
